import { Form, Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import GlobalStyles from '../../global-styles';
import API from '../../utils/api';
import {
  apiReponseError, apiRequestError, isValidJSONString, setAllFieldTouched
} from '../../utils/helper';
import { Box, Button, Grid, Paper, TextField, Typography } from '../../utils/material-ui-core';

interface EmailType {
  email: string;
}

const emailValidationDefaultValue = {
  email: "",
};

const emailFormValidationSchema = Yup.object().shape({
  email: Yup.string().required("Email is required."),
});

const defaultEmailValidationResult: EmailValidationResult = {
  valid: false,
  validationResult: {
    checks: {
      additional: {
        has_known_bounces: false,
        has_suspected_bounces: false,
      },
      domain: {
        has_mx_or_a_record: false,
        has_valid_address_syntax: false,
        is_suspected_disposable_address: false,
      },
      local_part: {
        is_suspected_role_address: false,
      },
    },
    email: "",
    friendlyMessage: "",
    host: "",
    ip_address: "",
    local: "",
    score: 0,
    source: "",
    valid: false,
    verdict: "",
  },
};

const EmailValidator = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const formReference = useRef(null) as any;

  const [initialEmailValidationFormValues, setInitialEmailValidationFormValues] = useState(emailValidationDefaultValue);
  const [loading, setLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState("");
  const [emailValidationResult, setEmailValidationResult] = useState(defaultEmailValidationResult);
  const [errorMessage, setErrorMessage] = useState("");

  const submitEmailValidation = async (values: EmailType, actions: any) => {
    const payload = {
      email: values.email,
    };

    setShowResult(false);
    setErrorMessage("");
    setResult("");
    setLoading(true);

    try {
      await API.post("/api/validation/emaildomain", payload)
        .then(async (response) => {
          try {
            if (isValidJSONString(response.data)) {
              const valid: boolean = response.data.valid;
              setEmailValidationResult(response.data);

              if (valid) {
                setResult("Email is valid");
              } else {
                setResult("Invalid Email");
              }
              setShowResult(true);
              setLoading(false);
            }
          } catch (e) {
            setErrorMessage(apiRequestError(e, "Something was wrong in our system."));
            setShowResult(true);
            setLoading(false);
          }
        })
        .catch((error) => {
          setErrorMessage(apiReponseError(error, "."));
          setShowResult(true);
          setLoading(false);
        });
    } catch (e) {
      setErrorMessage(apiRequestError(e, "Something was wrong in our system."));
      setShowResult(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    const initialPageAsync = async () => {
      setInitialEmailValidationFormValues(emailValidationDefaultValue);
    };
    initialPageAsync();
  }, []);

  return (
    <Box className={pageStyles.validatorContainer}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box className={globalStyles.topic}>Email Validator</Box>
        </Grid>
      </Grid>
      <Box p={2} className={pageStyles.inputContainer}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={6}>
            <Formik innerRef={formReference} initialValues={initialEmailValidationFormValues} validationSchema={emailFormValidationSchema} validateOnBlur={true} validateOnChange={true} onSubmit={submitEmailValidation}>
              {({ values, errors, handleChange, handleReset, setSubmitting, touched, handleBlur, isSubmitting }) => (
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    submitEmailValidation(values, { setSubmitting });
                  }}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={6} lg={6}>
                      <TextField
                        required
                        type="email"
                        id="email"
                        label="Email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth={true}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        error={touched.email && Boolean(errors.email)}
                        helperText={errors.email && touched.email ? errors.email : null}
                      />
                    </Grid>
                  </Grid>

                  <Box p={1}>&nbsp;</Box>
                  <Box p={3} pt={3} display="flex" justifyContent="center">
                    <Button
                      type="button"
                      id="button-clear-email"
                      color="default"
                      disabled={loading}
                      className={globalStyles.buttonDefault}
                      onClick={() => {
                        handleReset();
                        setShowResult(false);
                      }}
                    >
                      Clear
                    </Button>
                    <Button
                      type="submit"
                      id="button-save-email"
                      variant="contained"
                      color="secondary"
                      className={globalStyles.buttonDefault}
                      onClick={() => {
                        setAllFieldTouched(formReference, "", values);
                      }}
                    >
                      {loading ? "Checking..." : "Validate"}
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            {showResult ? (
              <>
                <Paper elevation={0} className={pageStyles.resultContainer}>
                  <Typography gutterBottom variant="h6">
                    Result
                  </Typography>
                  {errorMessage !== "" ? (
                    <Box display="flex" justifyContent="center">
                      <Typography gutterBottom variant="h6">
                        {errorMessage}
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Box display="flex" justifyContent="center">
                        <Typography gutterBottom variant="h6">
                          {result}
                        </Typography>
                      </Box>
                      <Box p={1}>&nbsp;</Box>

                      <Grid container>
                        <Grid item xs={6}>
                          <Box display="flex" flexDirection="column">
                            <Typography gutterBottom variant="body1">
                              Status
                            </Typography>
                            <Typography gutterBottom variant="h6">
                              <b>{emailValidationResult.valid ? "Valid" : "Invalid"}</b>
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={6}>
                          <Box display="flex" flexDirection="column">
                            <Typography gutterBottom variant="body1">
                              Source
                            </Typography>
                            <Typography gutterBottom variant="h6">
                              <b>{emailValidationResult.validationResult.source}</b>
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={6}>
                          <Box display="flex" flexDirection="column">
                            <Typography gutterBottom variant="body1">
                              Verdict
                            </Typography>
                            <Typography gutterBottom variant="h6">
                              <b>{emailValidationResult.validationResult.verdict}</b>
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={6}>
                          <Box display="flex" flexDirection="column">
                            <Typography gutterBottom variant="body1">
                              AI Score
                            </Typography>
                            <Typography gutterBottom variant="h6">
                              <b>{emailValidationResult.validationResult.score * 100}%</b>
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      <Grid container>
                        <Grid item xs={12}>
                          <Box className={globalStyles.topic}>Domain Checks</Box>
                        </Grid>
                      </Grid>

                      <Box display="flex" flexDirection="row">
                        <Typography gutterBottom variant="body1">
                          Valid addess Syntax &nbsp;
                        </Typography>
                        <Typography gutterBottom variant="body1" className={emailValidationResult.validationResult.checks.domain.has_valid_address_syntax ? pageStyles.successMessage : pageStyles.errorMessage}>
                          {emailValidationResult.validationResult.checks.domain.has_valid_address_syntax ? "PASS" : "FAIL"}
                        </Typography>
                      </Box>

                      <Typography gutterBottom variant="body2">
                        <small>If pass, then the address is a properly formatted email address (e.g. it has an @ sign and a top level domain). If fail, then it’s a malformed address.</small>
                        <br /> <br />
                      </Typography>

                      <Box display="flex" flexDirection="row">
                        <Typography gutterBottom variant="body1">
                          MX and/or A record present &nbsp;
                        </Typography>

                        <Typography gutterBottom variant="body1" className={emailValidationResult.validationResult.checks.domain.has_mx_or_a_record ? pageStyles.successMessage : pageStyles.errorMessage}>
                          <b>{emailValidationResult.validationResult.checks.domain.has_mx_or_a_record ? "PASS" : "FAIL"}</b>
                        </Typography>
                      </Box>
                      <Typography gutterBottom variant="body1">
                        <small>If pass, the domain on the address has all the necessary DNS records to deliver a message somewhere. If fail, the domain is missing the required DNS records and will result in a bounce if delivered to.</small>
                        <br />
                        <br />
                      </Typography>

                      <Grid container>
                        <Grid item xs={12}>
                          <Box className={globalStyles.topic}>Local Part Checks</Box>
                        </Grid>
                      </Grid>

                      <Box display="flex" flexDirection="row">
                        <Typography gutterBottom variant="body1">
                          Check for role address &nbsp;
                        </Typography>

                        <Typography gutterBottom variant="body1" className={!emailValidationResult.validationResult.checks.local_part.is_suspected_role_address ? pageStyles.successMessage : pageStyles.errorMessage}>
                          <b>{emailValidationResult.validationResult.checks.local_part.is_suspected_role_address ? "SUSPECTED" : "NOT SUSPECTED"}</b>
                        </Typography>
                      </Box>

                      <Typography gutterBottom variant="body2">
                        <small>If not suspected, the domain part of the email address appears to be from a disposable email address service, in which the addresses are only good for a short period of time.</small>
                        <br />
                        <br />
                      </Typography>

                      <Box display="flex" flexDirection="row">
                        <Typography gutterBottom variant="body1">
                          Check for disposable address &nbsp;
                        </Typography>

                        <Typography gutterBottom variant="body1" className={!emailValidationResult.validationResult.checks.domain.is_suspected_disposable_address ? pageStyles.successMessage : pageStyles.errorMessage}>
                          <b>{emailValidationResult.validationResult.checks.domain.is_suspected_disposable_address ? "SUSPECTED" : "NOT SUSPECTED"}</b>
                        </Typography>
                      </Box>

                      <Typography gutterBottom variant="body2">
                        <small>If not suspected, the domain part of the email address appears to be from a disposable email address service, in which the addresses are only good for a short period of time.</small>
                        <br /> <br />
                      </Typography>

                      <Grid container>
                        <Grid item xs={12}>
                          <Box className={globalStyles.topic}>Additional Checks</Box>
                        </Grid>
                      </Grid>

                      <Box display="flex" flexDirection="row">
                        <Typography gutterBottom variant="body1">
                          Conforms to ISP-specific grammer rules &nbsp;
                        </Typography>

                        <Typography gutterBottom variant="body1" className={!emailValidationResult.validationResult.checks.additional.has_suspected_bounces ? pageStyles.successMessage : pageStyles.errorMessage}>
                          <b>{!emailValidationResult.validationResult.checks.additional.has_suspected_bounces ? "PASS" : "FAIL"}</b>
                        </Typography>
                      </Box>

                      <Typography gutterBottom variant="body2">
                        <small> If pass, our machine learning model suspects that the email address might bounce.</small>
                        <br /> <br />
                      </Typography>

                      <Box display="flex" flexDirection="row">
                        <Typography gutterBottom variant="body1">
                          Check for known bounces &nbsp;
                        </Typography>

                        <Typography gutterBottom variant="body1" className={!emailValidationResult.validationResult.checks.additional.has_known_bounces ? pageStyles.successMessage : pageStyles.errorMessage}>
                          <b>{!emailValidationResult.validationResult.checks.additional.has_known_bounces ? "PASS" : "FAIL"}</b>
                        </Typography>
                      </Box>

                      <Typography gutterBottom variant="body2">
                        <small>If pass, the email address has previously been sent to through your SendGrid account and has resulted in a bounce.</small>
                        <br /> <br />
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </>
            ) : null}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    validatorContainer: {
      padding: theme.spacing(1),
      marginBottom: theme.spacing(4),
      height: "100%",
    },
    inputContainer: {
      color: theme.palette.type === "dark" ? "#ffffff" : "#000000",
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(2),
      margin: theme.spacing(0),
      height: "calc(100% - 70px)",
    },
    resultContainer: {
      backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
      padding: 8,
    },
    errorMessage: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
      fontWeight: "bolder",
      paddingLeft: 5,
      paddingRight: 5,
    },
    successMessage: {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,
      fontWeight: "bolder",
      paddingLeft: 5,
      paddingRight: 5,
    },
  })
);

export default EmailValidator;
